/* RETTERBOX pfand.js — automatic deposit (Pfand) cart reconciliation.
 *
 * Central & self-healing: after every cart mutation the deposit lines in the cart
 * are made to EXACTLY match the deposit obligation of the current product lines.
 *
 *   physical units per selling unit = "__SELL_<n>" suffix in the line SKU (native
 *   Shopify fixed-bundle convention; SELL_24 == 24 components) — otherwise 1.
 *   deposit relationship            = custom.pfand (value = deposit product title),
 *   carried per line in the `_pfand` property (+ page-load fallback map).
 *
 * Deposit lines are marked with private props (_system_deposit / _deposit_*) and are
 * never counted as sources, so they never trigger further deposits.
 *
 * Depends on window.RB_PFAND (snippets/pfand-config.liquid) and the theme's
 * `cart:updated` DOM event (assets/cart-drawer.js / theme.js), which is dispatched
 * by every add/change path and carries the cart JSON.
 */
(function () {
  'use strict';

  var CFG = window.RB_PFAND = window.RB_PFAND || {};
  CFG.map = CFG.map || {};
  CFG.cart = CFG.cart || {};
  CFG.systemVariantIds = CFG.systemVariantIds || [];
  CFG.systemProductIds = CFG.systemProductIds || [];

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' }).then(function (r) { return r.json(); });
  }
  // Physical units per selling unit. An explicit custom.verkaufseinheit (carried per
  // line in the _ve property) wins when > 1 — this covers a TRAY sold as a standalone
  // product (not a fixed bundle, so no __SELL_n). Otherwise the native "__SELL_n" bundle
  // suffix (bundle logic unchanged); otherwise 1.
  function sellUnits(it) {
    if (it && it.properties && it.properties._ve) {
      var ve = parseInt(it.properties._ve, 10);
      if (ve > 1) return ve;
    }
    var sku = it && it.sku ? it.sku : it;
    var m = /__SELL_(\d+)/.exec(sku || '');
    return m ? parseInt(m[1], 10) : 1;
  }
  function isSystemLine(it) {
    if (it.properties && it.properties._system_deposit === 'true') return true;
    return CFG.systemVariantIds.indexOf(it.variant_id) >= 0;
  }
  function pfandTitleFor(it) {
    if (it.properties && it.properties._pfand) return it.properties._pfand;
    if (CFG.cart[it.product_id]) return CFG.cart[it.product_id];
    return null;
  }
  function entryByVariant(vid) {
    var keys = Object.keys(CFG.map);
    for (var i = 0; i < keys.length; i++) {
      var e = CFG.map[keys[i]];
      if (e.variantId === vid) { e.title = keys[i]; return e; }
    }
    return null;
  }

  // Compute the required deposit quantities: { depositVariantId: qty } aggregated per type.
  function computeRequired(cart) {
    var required = {};
    (cart.items || []).forEach(function (it) {
      if (isSystemLine(it)) return;                                   // never a source
      if (CFG.systemProductIds.indexOf(it.product_id) >= 0) return;   // safety
      var pf = pfandTitleFor(it);
      if (!pf) return;                                                // no deposit
      var entry = CFG.map[pf];
      if (!entry || !entry.variantId) {
        console.error('[RB Pfand] Unresolvable custom.pfand "' + pf + '" — no deposit variant.', {
          product: it.product_title, sku: it.sku, variant_id: it.variant_id, pfand: pf
        });
        return;                                                       // never substitute another type
      }
      required[entry.variantId] = (required[entry.variantId] || 0) + sellUnits(it) * it.quantity;
    });
    return required;
  }

  var reconciling = false;   // recursion / re-entrancy guard
  var pending = false;       // a cart change arrived while we were mutating

  function reconcile() {
    if (reconciling) { pending = true; return Promise.resolve(null); }
    reconciling = true;
    // Clamp max-order-qty FIRST so deposits are computed on the clamped quantities,
    // never a transient over-limit amount. Quiet = clamp without its own broadcast.
    var pre = window.RB_clampCartQuiet ? window.RB_clampCartQuiet() : Promise.resolve();
    return pre.then(getCart).then(function (cart) {
      var required = computeRequired(cart);

      var existing = {};     // depositVariantId -> line (first seen)
      var duplicates = [];   // extra system lines for the same deposit type
      (cart.items || []).forEach(function (it) {
        if (!isSystemLine(it)) return;
        if (existing[it.variant_id]) duplicates.push(it); else existing[it.variant_id] = it;
      });

      var ops = [];
      Object.keys(required).forEach(function (dvStr) {
        var dv = parseInt(dvStr, 10), qty = required[dvStr], line = existing[dv];
        if (line) { if (line.quantity !== qty) ops.push({ type: 'change', id: line.key, quantity: qty }); }
        else ops.push({ type: 'add', id: dv, quantity: qty });
      });
      Object.keys(existing).forEach(function (dvStr) {
        var dv = parseInt(dvStr, 10);
        if (!required[dv]) ops.push({ type: 'change', id: existing[dv].key, quantity: 0 });
      });
      duplicates.forEach(function (dup) { ops.push({ type: 'change', id: dup.key, quantity: 0 }); });

      if (!ops.length) return null;   // already correct → no request, no recursion

      return ops.reduce(function (p, op) {
        return p.then(function () {
          if (op.type === 'add') {
            var entry = entryByVariant(op.id);
            var props = { _system_deposit: 'true' };
            if (entry) { props._deposit_product_gid = entry.gid; props._deposit_sku = entry.title; }
            return fetch('/cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: op.id, quantity: op.quantity, properties: props })
            });
          }
          return fetch('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: op.id, quantity: op.quantity })
          });
        });
      }, Promise.resolve())
        .then(getCart)
        .then(function (updated) {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: updated }));
          return updated;
        });
    }).catch(function (err) { console.error('[RB Pfand] reconciliation failed', err); })
      .finally(function () {
        reconciling = false;
        if (pending) { pending = false; reconcile(); }   // coalesce concurrent changes
      });
  }

  window.RB_reconcileDeposits = reconcile;

  // Run after every cart mutation (add/qty/remove all dispatch cart:updated).
  document.addEventListener('cart:updated', function () { if (!reconciling) reconcile(); });

  // Self-heal on load (cart page, cart drawer first paint, any page).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }

  // Best-effort: reconcile immediately before checkout.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cart-checkout], button[name="checkout"], a[href$="/checkout"]');
    if (!btn || btn.dataset.rbPfandChecked === '1') return;
    e.preventDefault();
    reconcile().then(function () {
      btn.dataset.rbPfandChecked = '1';
      if (btn.tagName === 'A') window.location.href = btn.getAttribute('href');
      else btn.click();
    });
  }, true);
})();
